//
// Copyright (c) 2011-2026 by Curalate, Inc.
//

import { DateUtils } from 'unison';

import DateString, { DATE_STRING_FORMAT } from '@models/DateString';
import Interval from '@models/Interval';
import TimeZoneId from '@models/TimeZoneId';
import { IUnisonDateRange } from '@models/UnisonTypes';
import { ITrackableComponent } from '@utils/withTracking';

// TODO: Implement legacy interface DEV-59292
export interface ILegacyDateRangePickerProps extends ITrackableComponent {
  className?: string;
  value: DateUtils.IUnisonDateRange;
  onDateRangeChanged: (dateRange: DateUtils.IUnisonDateRange) => void;
  minDate?: Date;
  maxDate?: Date;
  maxDateRange?: number;
  aliases?: DateUtils.UnisonDateRangeAlias[];
  footnote?: string;
  isDisabled?: boolean;
}

export interface IAliasedDateRange {
  displayName: string; // TODO: PD-306207 - Make this a bounded type with DateRangeAliasDisplayNames and other custom names
  startDate: DateString;
  endDate: DateString;
  timeZone: TimeZoneId;
}

export type DateRangeAlias =
  | Exclude<DateUtils.UnisonDateRangeAlias, 'custom'>
  | 'next7Days'
  | 'next30Days'
  | 'next90Days';

type DateRangeAliasKey = keyof typeof DateRangeAliasDisplayNames;

export const CUSTOM_DATE_RANGE_DISPLAY_NAME = 'Custom';
export const CUSTOM_DATE_RANGE_SEPARATOR = '/';
const CUSTOM_DATE_RANGE_REGEX = /^\d{4}-\d{2}-\d{2}\/\d{4}-\d{2}-\d{2}$/;

export const DateRangeAliasDisplayNames: Record<DateRangeAlias, string> = {
  next7Days: 'Next 7 Days',
  next30Days: 'Next 30 Days',
  next90Days: 'Next 90 Days',
  last24Hours: 'Last 24 Hours',
  last7Days: 'Last 7 Days',
  last30Days: 'Last 30 Days',
  last90Days: 'Last 90 Days',
  last3Months: 'Last 3 Months',
  last1Year: 'Last 1 Year',
  today: 'Today',
  allTime: 'All Time',
} as const;

export function getAliasedDateRange(
  alias: DateRangeAlias,
  timeZone: TimeZoneId,
): IAliasedDateRange {
  const today = DateString.getToday(timeZone);

  const endDate = (() => {
    switch (alias) {
      case 'next7Days': {
        return DateString.addDays(today, 7);
      }
      case 'next30Days': {
        return DateString.addDays(today, 30);
      }
      case 'next90Days': {
        return DateString.addDays(today, 90);
      }
      default:
        return today;
    }
  })();

  const startDate = (() => {
    switch (alias) {
      case 'last7Days': {
        return DateString.addDays(today, -6);
      }
      case 'last30Days': {
        return DateString.addDays(today, -29);
      }
      case 'last90Days': {
        return DateString.addDays(today, -89);
      }
      case 'last3Months': {
        return DateString.addMonths(today, -3);
      }
      case 'last1Year': {
        return DateString.addYears(today, -1);
      }
      case 'allTime': {
        // Use UTC for this, so we never end up with a date before "0" unixtime.
        // If we use a timezone west of UTC, we will get 1969-12-31,
        // which as start-of-day timestamp will be a negative unixtime.
        return DateString.fromTimestamp(0, TimeZoneId.Utc);
      }
      default: {
        return today;
      }
    }
  })();

  return {
    displayName: DateRangeAliasDisplayNames[alias],
    timeZone,
    startDate,
    endDate,
  };
}

const getReadableDate = (date: DateString) =>
  DateString.format(date, 'MM/DD/YY');

export const getReadableDateRangeValue = ({
  displayName,
  endDate,
  startDate,
}: IAliasedDateRange) => {
  return displayName === CUSTOM_DATE_RANGE_DISPLAY_NAME
    ? `${getReadableDate(startDate)} - ${getReadableDate(endDate)}`
    : displayName;
};

const getDateRangeAliasKeys = (): DateRangeAliasKey[] => {
  return Object.keys(DateRangeAliasDisplayNames) as DateRangeAliasKey[];
};

export const getStringFormatDateRangeValue = ({
  displayName,
  endDate,
  startDate,
}: IAliasedDateRange) => {
  if (displayName === CUSTOM_DATE_RANGE_DISPLAY_NAME) {
    return Interval.fromDateStrings(startDate, endDate);
  }

  return getDateRangeAliasKeys().find(
    (key) => DateRangeAliasDisplayNames[key] === displayName,
  );
};

export const getDefaultDateRangePickerAliases = (timeZone: TimeZoneId) => {
  return (['last7Days', 'last30Days', 'last3Months', 'allTime'] as const).map(
    (alias) => getAliasedDateRange(alias, timeZone),
  );
};

export const getAliasedDateRangeFromUnisonDateRange = (
  dateRange: IUnisonDateRange,
  timeZone: TimeZoneId,
): IAliasedDateRange => {
  return {
    displayName:
      dateRange.alias === 'custom'
        ? CUSTOM_DATE_RANGE_DISPLAY_NAME
        : DateRangeAliasDisplayNames[dateRange.alias],
    startDate: DateString.fromUnisonDateRange(dateRange).startDate,
    endDate: DateString.fromUnisonDateRange(dateRange).endDate,
    timeZone,
  };
};

/**
 * Get aliased date range from valid custom date range.
 * @param {string} dateRange Valid custom date range
 * @param {TimeZoneId} timeZone User's time zone
 * @returns {IAliasedDateRange} Aliased date range object
 */
export const getAliasedDateRangeFromCustomDateRange = (
  dateRange: string,
  timeZone: TimeZoneId,
): IAliasedDateRange => {
  const [startDate, endDate] = dateRange.split(CUSTOM_DATE_RANGE_SEPARATOR) as [
    string,
    string,
  ];

  return {
    displayName: CUSTOM_DATE_RANGE_DISPLAY_NAME,
    endDate: DateString.fromString(endDate, DATE_STRING_FORMAT),
    startDate: DateString.fromString(startDate, DATE_STRING_FORMAT),
    timeZone,
  };
};

/**
 * Validate if string is a valid custom date range in the format `YYYY-MM-DD/YYYY-MM-DD`.
 * @param {string} dateRange String to be validated
 * @returns {boolean} `true` if string is a valid custom date range
 */
export const isValidCustomDateRange = (dateRange: string) => {
  return CUSTOM_DATE_RANGE_REGEX.test(dateRange);
};

/**
 * Get display name of date range alias.
 * @param {DateRangeAlias} alias Date range alias
 * @returns {string} Display name of date range alias
 */
export const getDateRangeAliasDisplayName = (alias: DateRangeAlias) =>
  DateRangeAliasDisplayNames[alias];

/**
 * Checks if the first date comes on or after the second date.
 * @param dateToCheck
 * @param referenceDate
 * @returns
 */
function getIsDateOnOrAfter(
  dateToCheck: DateString,
  referenceDate: DateString,
): boolean {
  return DateString.compare(dateToCheck, referenceDate) >= 0;
}

/**
 * gets valid date range aliases based on an if they are on or after the given earliest date.
 * @param earliestDate the ealiest date we have data for
 * @param timeZoneId clients time zone
 * @returns a list of valid aliases
 */
export function getOptionalDateRangeAliases(
  earliestDate: DateString,
  timeZoneId: TimeZoneId,
): IAliasedDateRange[] {
  const nowDateString = DateString.fromTimestamp(new Date(), timeZoneId);

  const sevenDaysAgoDateString = DateString.addDays(nowDateString, -7);
  const thirtyDaysAgoDateString = DateString.addDays(nowDateString, -30);
  const threeMonthsAgoDateString = DateString.addMonths(nowDateString, -3);
  const oneYearAgoDateString = DateString.addYears(nowDateString, -1);

  return [
    ...(getIsDateOnOrAfter(sevenDaysAgoDateString, earliestDate)
      ? [getAliasedDateRange('last7Days', timeZoneId)]
      : []),
    ...(getIsDateOnOrAfter(thirtyDaysAgoDateString, earliestDate)
      ? [getAliasedDateRange('last30Days', timeZoneId)]
      : []),
    ...(getIsDateOnOrAfter(threeMonthsAgoDateString, earliestDate)
      ? [getAliasedDateRange('last3Months', timeZoneId)]
      : []),
    ...(getIsDateOnOrAfter(oneYearAgoDateString, earliestDate)
      ? [getAliasedDateRange('last1Year', timeZoneId)]
      : []),
  ];
}

/**
 * Given a start and end date, returns an aliased date range object.
 */
export function getAliasedDateRangeByDates(
  dateRange: {
    startDate: DateString;
    endDate: DateString;
  },
  timeZone: TimeZoneId,
): IAliasedDateRange {
  const today = DateString.getToday(timeZone);
  const { endDate, startDate } = dateRange;

  const displayName = (() => {
    if (DateString.compare(startDate, today) === 0) {
      const dateDiff = DateString.getDayOffset(startDate, endDate);
      switch (dateDiff) {
        case 0:
          return DateRangeAliasDisplayNames.today;
        case 6:
          return DateRangeAliasDisplayNames.next7Days;
        case 29:
          return DateRangeAliasDisplayNames.next30Days;
        case 89:
          return DateRangeAliasDisplayNames.next90Days;
        default:
          return CUSTOM_DATE_RANGE_DISPLAY_NAME;
      }
    }
    if (DateString.compare(endDate, today) === 0) {
      const threeMonthsAgoDateString = DateString.addMonths(today, -3);
      const oneYearAgoDateString = DateString.addYears(today, -1);
      if (DateString.compare(startDate, threeMonthsAgoDateString) === 0) {
        return DateRangeAliasDisplayNames.last3Months;
      }
      if (DateString.compare(startDate, oneYearAgoDateString) === 0) {
        return DateRangeAliasDisplayNames.last1Year;
      }
      const dateDiff = DateString.getDayOffset(startDate, endDate);
      switch (dateDiff) {
        case 0:
          return DateRangeAliasDisplayNames.today;
        case 1:
          return DateRangeAliasDisplayNames.last24Hours;
        case 6:
          return DateRangeAliasDisplayNames.last7Days;
        case 29:
          return DateRangeAliasDisplayNames.last30Days;
        case 89:
          return DateRangeAliasDisplayNames.last90Days;
      }
    }
    return CUSTOM_DATE_RANGE_DISPLAY_NAME;
  })();

  return {
    displayName,
    timeZone,
    startDate,
    endDate,
  };
}

/**
 * Validate if string is a supported date range alias.
 * @param {string} dateRangeString String to be validated
 * @returns {boolean} `true` if string is a supported date range alias
 */
export const isDateRangeAlias = (
  dateRangeString: string,
): dateRangeString is DateRangeAlias => {
  return Object.keys(DateRangeAliasDisplayNames).includes(dateRangeString);
};

/**
 * Validate if date strings form a valid chronological sequence.
 * @param startDate Start date in YYYY-MM-DD format
 * @param endDate End date in YYYY-MM-DD format
 * @param maxDate Maximum allowed date for the end date
 * @returns `true` if startDate <= endDate <= maxDate
 */
export const isValidDateSequence = (
  startDate: string,
  endDate: string,
  maxDate: DateString,
): boolean => {
  const startDateString = DateString.fromString(startDate, 'YYYY-MM-DD');
  const endDateString = DateString.fromString(endDate, 'YYYY-MM-DD');

  return (
    DateString.compare(startDateString, endDateString) <= 0 &&
    DateString.compare(endDateString, maxDate) <= 0
  );
};
